import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Button } from '@mantine/core';
import { Link } from 'react-router-dom';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import AppStack from '../../common/AppStack';
import { triggerNotification } from '../../../helpers/notification';
import CustomNumberInput from '../../common/CustomNumberInput';
import AppText from '../../common/AppText';
import {
  ECOM_PRODUCT_PRICING_TYPE_ENUM,
  ECOM_PRODUCT_STATUS_ENUM
} from '../../../config/constants';
import AppCheckbox from '../../common/AppCheckbox';
import AppFlexbox from '../../common/AppFlexbox';
import ResourceSelectModal from '../../common/ResourceSelectModal';
import { singularPluralFormat } from '../../../helpers/format';

const StoreProductPricingModal = ({
  ecomStoreProduct,
  isOpen,
  onClose,
  selectedProductIds,
  filter,
  totalItemCount,
  selectAllDisabled,
  onSuccess
}) => {
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchStoreProductOptionsSummary,
    updateStoreProducts
  } = useContext(StoreContext);
  const [formState, setFormState] = useState({
    selectType: '',
    percentagePricing: true,
    pricingAmount: '',
    roundingAdjustment: '',
    isTaxExempt: false,
    setProductToLive: false,
    loading: false
  });

  useEffect(() => {
    if (isOpen) {
      const minPricingVariant = ecomStoreProduct?.ecomStoreProductVariants.reduce(
        (minObj, obj) =>
          obj.pricingAmount < minObj.pricingAmount ? obj : minObj,
        ecomStoreProduct.ecomStoreProductVariants[0]
      );

      setFormState({
        selectType: '',
        percentagePricing: minPricingVariant?.percentagePricing || true,
        pricingAmount: minPricingVariant
          ? minPricingVariant.pricingAmount / 100
          : '',
        roundingAdjustment: minPricingVariant?.roundingAdjustment || '',
        isTaxExempt: ecomStoreProduct?.isTaxExempt || false,
        setProductToLive: false,
        loading: false
      });
    }
  }, [isOpen]);

  const onSuccessCallback = () => {
    onClose();
    triggerNotification('Pricing updated!', 'success');
    if (onSuccess) {
      onSuccess();
    }
  };

  const onErrorCallback = (error) => {
    triggerNotification(error);
    setFormState({
      ...formState,
      loading: false
    });
  };

  return (
    <ResourceSelectModal
      filter={filter}
      formSectionProps={{
        isLoading: formState.loading,
        onCancel: onClose,
        onSubmit: () => {
          const updateData = {
            percentagePricing: formState.percentagePricing,
            pricingAmount: Math.round(formState.pricingAmount * 100),
            roundingAdjustment: formState.roundingAdjustment,
            rebaseAllPricing: true,
            isTaxExempt: formState.isTaxExempt,
            fkEcomPricingType: formState.percentagePricing
              ? ECOM_PRODUCT_PRICING_TYPE_ENUM.PERCENTAGE_ADJUSTMENT
              : ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT,
            fkEcomProductStatus: formState.setProductToLive
              ? ECOM_PRODUCT_STATUS_ENUM.ACTIVE
              : null
          };

          const productIds = ecomStoreProduct
            ? [ecomStoreProduct.uuid]
            : formState.selectType === 'all'
            ? state.ecomStoreProductSummaryOptions.value.ecomStoreProducts.map(
                (p) => p.uuid
              )
            : selectedProductIds;

          setFormState({
            ...formState,
            loading: true
          });
          updateStoreProducts(
            authState.pkEcomStore,
            {
              ecomStoreProducts: productIds.map((uuid) => ({
                uuid,
                ...updateData
              })),
              selectFirstItem: !!ecomStoreProduct,
              skipResponse: !ecomStoreProduct
            },
            onSuccessCallback,
            onErrorCallback
          );
        },
        submitTitle: ecomStoreProduct ? 'Save changes' : 'Set pricing'
      }}
      isLoading={state.ecomStoreProductSummaryOptions.loading}
      isOpen={isOpen}
      onClose={onClose}
      onSelect={(selectType) => {
        setFormState({ ...formState, selectType });
        if (selectType === 'all') {
          fetchStoreProductOptionsSummary(
            authState.pkEcomStore,
            filter,
            null,
            triggerNotification
          );
        }
      }}
      selectedIds={selectedProductIds}
      selectLabel="Set pricing for"
      selectLabelDescription="Select the products you want to set pricing for."
      size={450}
      skipSelect={
        selectAllDisabled || !!ecomStoreProduct || !selectedProductIds?.length
      }
      title="Set product pricing"
      totalItemCount={totalItemCount}
    >
      <AppStack style={{ gap: 8 }}>
        {!ecomStoreProduct && (
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Setting pricing for{' '}
            {singularPluralFormat(
              formState.selectType === 'all'
                ? state.ecomStoreProductSummaryOptions.value?.ecomStoreProducts
                    .length
                : selectedProductIds?.length,
              'product',
              'products'
            )}
            .
          </AppText>
        )}

        <AppStack>
          <CustomNumberInput
            allowNegative={false}
            decimalScale={2}
            disabled={formState.loading}
            fixedDecimalScale
            label={`Margin (in ${formState.percentagePricing ? '%' : '$'})`}
            leftSection={
              !formState.percentagePricing && (
                <AppText
                  style={{
                    fontWeight: 500,
                    color: '#666',
                    fontSize: 14
                  }}
                >
                  $
                </AppText>
              )
            }
            onChange={(v) =>
              setFormState({
                ...formState,
                pricingAmount: v
              })
            }
            placeholder="30.00"
            required
            rightSection={
              formState.percentagePricing ? (
                <AppText
                  style={{
                    fontWeight: 500,
                    color: '#666',
                    fontSize: 14
                  }}
                >
                  %
                </AppText>
              ) : (
                <></>
              )
            }
            value={formState.pricingAmount}
          />

          <Button.Group>
            <Button
              color={formState.percentagePricing ? 'blue' : 'dark'}
              disabled={formState.loading}
              onClick={() => {
                setFormState({
                  ...formState,
                  percentagePricing: true
                });
              }}
              size="compact-md"
              style={{ flex: 1 }}
              variant={formState.percentagePricing ? 'filled' : 'light'}
            >
              %
            </Button>
            <Button
              color={!formState.percentagePricing ? 'blue' : 'dark'}
              disabled={formState.loading}
              onClick={() => {
                setFormState({
                  ...formState,
                  percentagePricing: false
                });
              }}
              size="compact-md"
              style={{ flex: 1 }}
              variant={!formState.percentagePricing ? 'filled' : 'light'}
            >
              $
            </Button>
          </Button.Group>

          <CustomNumberInput
            allowNegative={false}
            clampBehavior="strict"
            decimalScale={0}
            disabled={formState.loading}
            fixedDecimalScale
            label="Rounding Adjustment (in cents)"
            max={99}
            min={0}
            onChange={(v) => {
              setFormState({
                ...formState,
                roundingAdjustment: v > 99 ? 99 : v
              });
            }}
            placeholder=".95"
            required
            value={
              formState.roundingAdjustment > 99
                ? 99
                : formState.roundingAdjustment
            }
          />

          <AppStack style={{ gap: 0 }}>
            <AppFlexbox
              onClick={() => {
                if (!formState.loading) {
                  setFormState({
                    ...formState,
                    isTaxExempt: !formState.isTaxExempt
                  });
                }
              }}
              style={{ gap: 8, cursor: 'pointer' }}
            >
              <AppCheckbox
                checked={formState.isTaxExempt}
                disabled={formState.loading}
                onChange={() => {}}
              />
              <AppStack style={{ gap: 0 }}>
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  Tax exempt
                </AppText>
                <AppText style={{ fontSize: 13, color: '#666' }}>
                  Exempt {ecomStoreProduct ? 'this product' : 'products'} from
                  tax charges
                </AppText>

                <AppFlexbox>
                  <Anchor
                    component={Link}
                    style={{ fontSize: 13, color: 'dodgeblue' }}
                    to="/merchant/settings/taxes"
                  >
                    Review your tax settings
                  </Anchor>
                </AppFlexbox>
              </AppStack>
            </AppFlexbox>
          </AppStack>

          {(!ecomStoreProduct ||
            ecomStoreProduct?.fkEcomProductStatus.toString() !==
              ECOM_PRODUCT_STATUS_ENUM.ACTIVE) && (
            <AppCheckbox
              checked={formState.setProductToLive}
              disabled={formState.loading}
              label={
                ecomStoreProduct
                  ? 'Set product to live'
                  : 'Set products to live'
              }
              onChange={() =>
                setFormState({
                  ...formState,
                  setProductToLive: !formState.setProductToLive
                })
              }
              styles={{ label: { fontWeight: 500 } }}
            />
          )}

          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            This will apply the pricing to all product variants.
          </AppText>
        </AppStack>
      </AppStack>
    </ResourceSelectModal>
  );
};

StoreProductPricingModal.propTypes = {
  ecomStoreProduct: PropTypes.object,
  filter: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  selectAllDisabled: PropTypes.bool,
  selectedProductIds: PropTypes.array,
  totalItemCount: PropTypes.number
};

export default StoreProductPricingModal;
